import { create } from "zustand";
import { apiStore, ASSETS_ROOT_TEST } from "./api.store";
import { useAuthStore } from "./auth.store";

export interface Document {
	name: string;
	mimeType: string;
	url: string;
	key: string;
	originalName: string;
}

const assetsRoot = window.__env__?.ASSETS_ROOT || ASSETS_ROOT_TEST;

interface MediaStore {
	profilePicture: string;
	document?: Document;
	documentList?: Document[];
	loading: boolean;
	documentLoading: boolean;

	uploadProfilePicture(file: string): Promise<string | false>;
	uploadDocumentList(files: string[]): Promise<false | undefined>;
	uploadDocument(file: string): Promise<false | undefined>;
	setProfilePicture(profilePicture: string): void;
	removeImage(): void;
	removeDocument(): void;
	removeDocumentList(): void;
}

export const useMediaStore = create<MediaStore>()((set) => ({
	profilePicture: "",
	document: undefined,
	documentList: undefined,
	loading: false,
	documentLoading: false,

	async uploadProfilePicture(file) {
		const orgId = useAuthStore.getState().orgId;

		try {
			set({ loading: true });
			const { data } = await apiStore.postFormData({
				path: "/upload",
				query: { path: "/" + orgId },
				body: { file },
				isAuthRequired: true,
				mediaType: "image",
			});
			const profilePicture = assetsRoot + "/image/" + data.key;
			set({ profilePicture });
			return profilePicture;
		} catch (error) {
			return false;
		} finally {
			set({ loading: false });
		}
	},

	async uploadDocumentList(files) {
		const orgId = useAuthStore.getState().orgId;

		try {
			set({ documentLoading: true });
			const docList: Document[] = [];

			for (const file of files) {
				const { data } = await apiStore.postFormData({
					path: `/upload`,
					query: { path: "/" + orgId },
					body: { file },
					isAuthRequired: true,
					mediaType: "application",
				});

				data.url = assetsRoot + "/file/" + data.key;
				docList.push(data);
			}

			set({ documentList: docList });
		} catch (error) {
			return false;
		} finally {
			set({ documentLoading: false });
		}
	},

	async uploadDocument(file) {
		const orgId = useAuthStore.getState().orgId;

		try {
			set({ documentLoading: true });
			const { data } = await apiStore.postFormData({
				path: `/upload`,
				query: { path: "/" + orgId },
				body: { file },
				isAuthRequired: true,
				mediaType: "application",
			});

			data.url = assetsRoot + "/file/" + data.key;

			set({ document: data });
		} catch (error) {
			return false;
		} finally {
			set({ documentLoading: false });
		}
	},

	setProfilePicture(profilePicture) {
		set({ profilePicture });
	},

	removeImage() {
		set({ profilePicture: "" });
	},

	removeDocument() {
		set({ document: undefined });
	},

	removeDocumentList() {
		set({ documentList: undefined });
	},
}));
